import { useRef, useState } from 'react';

import { TaskUpdateStrategy } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useAuthContext } from '@/views/auth/Auth';
import { useGlobalAccount } from '@/views/household/household-queries';
import { useAttachTaskToHousehold, useTaskView, useUpdateTask } from '@/views/tasks/task-queries';
import { TaskForm, TaskFormRef } from '@/views/tasks/widgets/TaskForm';
import { TaskViewDetailsContent } from '@/views/tasks/widgets/TaskViewDetailsContent';
import { Toast } from 'widgets';

import { useDockState } from '../dock-state';
import { UpdateTaskDockItem as UpdateTaskDockItemType } from '../dock-state-types';

export const TaskDockItem = (props: { item: UpdateTaskDockItemType }) => {
    const { currentPersonOrgGUID } = useAuthContext();
    const { data: globalAccount } = useGlobalAccount(props.item.data.globalAccountGUID);
    const { data: taskView, isLoading } = useTaskView(props.item.data.taskGUID);
    const { updateDockItem } = useDockState();
    const formRef = useRef<TaskFormRef>(null);
    const updateTaskMutation = useUpdateTask();
    const attachTaskMutation = useAttachTaskToHousehold();
    const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
    const [pendingAttachHouseholdGUID, setPendingAttachHouseholdGUID] = useState<string | undefined>(undefined);
    const [commentDraft, setCommentDraft] = useState(props.item.data.commentDraft ?? '');

    const viewMode = props.item.data.viewMode ?? 'view';

    const setViewMode = (mode: 'edit' | 'view') => {
        updateDockItem({ ...props.item, data: { ...props.item.data, viewMode: mode } });
    };

    type UpdateArg = Parameters<typeof updateTaskMutation.mutateAsync>[0];
    type PendingUpdate = Omit<UpdateArg, 'taskGUID' | 'taskUpdateStrategy'>;
    const [pendingData, setPendingData] = useState<PendingUpdate | null>(null);

    if (props.item.data.globalAccountGUID && !globalAccount) return null;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Loading task details...</div>
            </div>
        );
    }

    if (!taskView) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Task not found.</div>
            </div>
        );
    }

    const doUpdate = (data: PendingUpdate, strategy?: TaskUpdateStrategy, attachHouseholdGUID?: string) => {
        updateTaskMutation
            .mutateAsync({ ...data, taskGUID: props.item.data.taskGUID, taskUpdateStrategy: strategy })
            // Attaching a tenant-only task to a household is a dedicated, sticky operation.
            .then(() =>
                attachHouseholdGUID
                    ? attachTaskMutation.mutateAsync({
                          taskGUID: props.item.data.taskGUID,
                          householdGUID: attachHouseholdGUID,
                      })
                    : undefined,
            )
            .then(() => {
                Toast.success(`Task "${(data.title || 'Untitled Task').slice(0, 20)}" updated successfully`);
                formRef.current?.formMethods.reset(formRef.current.formMethods.getValues());
                updateDockItem({
                    ...props.item,
                    openedAt: new Date().toISOString(),
                    data: { ...props.item.data, viewMode: 'view' },
                });
            })
            .catch(() => {
                Toast.forError(new Error('Failed to update task'), 'Failed to update task');
            })
            .finally(() => {
                setStrategyDialogOpen(false);
                setPendingData(null);
                setPendingAttachHouseholdGUID(undefined);
            });
    };

    if (viewMode === 'view') {
        return (
            <TaskViewDetailsContent
                task={taskView}
                currentPersonOrgGUID={currentPersonOrgGUID}
                commentDraft={commentDraft}
                onCommentDraftChange={setCommentDraft}
            />
        );
    }

    return (
        <div
            onBlur={() => {
                const formValues = formRef.current?.formMethods.getValues();
                if (formValues) {
                    updateDockItem({
                        ...props.item,
                        data: {
                            ...props.item.data,
                            title: formValues.title || '',
                            description: formValues.description || '',
                            householdOption: formValues.householdOption,
                            assigneeOption: formValues.assigneeOption || { value: '', label: '' },
                            taskTypeOption: formValues.taskTypeOption || { value: 'GENERAL', label: 'General' },
                            taskStatusOption: formValues.taskStatusOption || { value: 'NEW', label: 'New' },
                            dueDate: formValues.dueDate || '',
                            isRecurring: !!formValues.isRecurring,
                            frequencyOption: formValues.frequencyOption,
                            commentDraft,
                        },
                    });
                }
            }}
        >
            <div className="rounded-md border p-4">
                <TaskForm
                    key={props.item.id}
                    ref={formRef}
                    // Household is sticky once set: only a tenant-only task may be attached to a
                    // household; a household-scoped task keeps its household locked.
                    householdMode={taskView.householdGUID ? 'locked' : 'attach'}
                    onSubmit={(data) => {
                        if (updateTaskMutation.isPending) return;
                        if ('householdGuids' in data) {
                            Toast.forError(new Error('Unexpected fan-out form output when updating a task'));
                            return;
                        }
                        // Field edits go through updateTask. A tenant-only task that picked a
                        // household is additionally attached (dedicated, sticky, whole-series).
                        const attachHouseholdGUID =
                            !taskView.householdGUID && !('noHousehold' in data) ? data.householdGuid : undefined;
                        if (taskView.frequency) {
                            setPendingData(data);
                            setPendingAttachHouseholdGUID(attachHouseholdGUID);
                            setStrategyDialogOpen(true);
                        } else {
                            doUpdate(data, undefined, attachHouseholdGUID);
                        }
                    }}
                    defaultTaskView={taskView}
                    defaultFormValues={props.item.data}
                    onCancel={() => setViewMode('view')}
                    householdGUID={props.item.data.globalAccountGUID}
                />
            </div>

            <Dialog open={strategyDialogOpen} onOpenChange={setStrategyDialogOpen}>
                <DialogContent className="overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Update recurring task</DialogTitle>
                        <DialogDescription>
                            Do you want to update only this task, or this and all future tasks in the series?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setStrategyDialogOpen(false);
                                setPendingData(null);
                                setPendingAttachHouseholdGUID(undefined);
                            }}
                            disabled={updateTaskMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() =>
                                pendingData &&
                                doUpdate(pendingData, TaskUpdateStrategy.THIS_ONLY, pendingAttachHouseholdGUID)
                            }
                            disabled={updateTaskMutation.isPending}
                        >
                            This task only
                        </Button>
                        <Button
                            onClick={() =>
                                pendingData &&
                                doUpdate(pendingData, TaskUpdateStrategy.THIS_AND_FUTURE, pendingAttachHouseholdGUID)
                            }
                            disabled={updateTaskMutation.isPending}
                        >
                            This and future tasks
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
